import { useTranslation } from 'react-i18next'
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Legend,
    CartesianGrid,
} from 'recharts'
import { CHART_COLORS, CHART_THEME } from '@/constants/chartColors'
import { QuarterlyMetrics } from '@/modules/Survey/types/quarterly-report'
import { useLegendToggle } from '../hooks/use-legend-toggle'

interface SatisfactionRatingsChartProps {
    data: QuarterlyMetrics[]
}

export const SatisfactionRatingsChart = ({
    data,
}: SatisfactionRatingsChartProps) => {
    const { t } = useTranslation('surveys')
    const ratesToggle = useLegendToggle()
    const ratingsToggle = useLegendToggle()

    const highLabel = t('surveys.quarterly_report.high_satisfaction')
    const medLabel = t('surveys.quarterly_report.medium_satisfaction')
    const svcLabel = t('surveys.quarterly_report.service_rating')
    const careLabel = t('surveys.quarterly_report.care_rating')
    const techLabel = t('surveys.quarterly_report.tech_rating')
    const issueLabel = t('surveys.quarterly_report.issue_rating')
    const referralLabel = t('surveys.quarterly_report.referral_rating')

    const ratesData = data.map((row) => ({
        quarter: row.quarter,
        [highLabel]: row.high_satisfaction_rate,
        [medLabel]: row.medium_satisfaction_rate,
    }))

    const ratingsData = data.map((row) => ({
        quarter: row.quarter,
        [svcLabel]: row.avg_service_rating,
        [careLabel]: row.avg_customer_care_rating,
        [techLabel]: row.avg_tech_support_rating,
        [issueLabel]: row.avg_issue_resolution_rating,
        [referralLabel]: row.avg_referral_willingness_rating,
    }))

    const ratingLines = [
        { key: svcLabel, color: CHART_COLORS.PRIMARY_BLUE },
        { key: careLabel, color: CHART_COLORS.INDIGO_PURPLE },
        { key: techLabel, color: CHART_COLORS.SKY_CYAN },
        { key: issueLabel, color: CHART_COLORS.PINK_FUCHSIA },
        { key: referralLabel, color: CHART_COLORS.EMERALD_GREEN },
    ]

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {/* Satisfaction rates — stacked bar */}
            <div className="bg-white rounded-lg p-4 border border-gray-100">
                <h3 className="font-semibold text-gray-700 mb-4">
                    {t('surveys.quarterly_report.chart_satisfaction_rates')}
                </h3>
                <ResponsiveContainer width="100%" height={280}>
                    <BarChart
                        data={ratesData}
                        margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                    >
                        <CartesianGrid {...CHART_THEME.grid} />
                        <XAxis
                            dataKey="quarter"
                            {...CHART_THEME.axis}
                            tickLine={false}
                        />
                        <YAxis {...CHART_THEME.axis} unit="%" />
                        <Tooltip
                            contentStyle={CHART_THEME.tooltip.contentStyle}
                        />
                        <Legend
                            wrapperStyle={{
                                fontSize: '12px',
                                paddingTop: '8px',
                                cursor: 'pointer',
                            }}
                            iconSize={10}
                            onClick={ratesToggle.handleLegendClick}
                        />
                        <Bar
                            dataKey={highLabel}
                            stackId="satisfaction"
                            fill={CHART_COLORS.EMERALD_GREEN}
                            radius={[0, 0, 0, 0]}
                            hide={ratesToggle.isHidden(highLabel)}
                        />
                        <Bar
                            dataKey={medLabel}
                            stackId="satisfaction"
                            fill={CHART_COLORS.AMBER_ORANGE}
                            radius={[3, 3, 0, 0]}
                            hide={ratesToggle.isHidden(medLabel)}
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>

            {/* Service ratings — line chart */}
            <div className="bg-white rounded-lg p-4 border border-gray-100">
                <h3 className="font-semibold text-gray-700 mb-4">
                    {t('surveys.quarterly_report.chart_service_ratings')}
                </h3>
                <ResponsiveContainer width="100%" height={280}>
                    <LineChart
                        data={ratingsData}
                        margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                    >
                        <CartesianGrid {...CHART_THEME.grid} />
                        <XAxis
                            dataKey="quarter"
                            {...CHART_THEME.axis}
                            tickLine={false}
                        />
                        <YAxis {...CHART_THEME.axis} domain={[0, 10]} />
                        <Tooltip
                            contentStyle={CHART_THEME.tooltip.contentStyle}
                        />
                        <Legend
                            wrapperStyle={{
                                fontSize: '11px',
                                paddingTop: '8px',
                                cursor: 'pointer',
                            }}
                            iconSize={10}
                            onClick={ratingsToggle.handleLegendClick}
                        />
                        {ratingLines.map(({ key, color }) => (
                            <Line
                                key={key}
                                type="monotone"
                                dataKey={key}
                                stroke={color}
                                strokeWidth={2}
                                dot={{ r: 4 }}
                                activeDot={{ r: 6 }}
                                hide={ratingsToggle.isHidden(key)}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    )
}
